/**
 * Ajustes de preço — caixa de aprovação + auditoria (#274 + #284).
 *
 * Gerente/Dono veem os pedidos de ajuste dos garçons em tempo real
 * (evento `adjustment.requested`), aprovam (aplica o ajuste) ou recusam com
 * motivo. A aba Auditoria mostra quem pediu, quem decidiu, quando, de → para
 * e o motivo — ajustes aplicados e pedidos recusados/cancelados.
 */
import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adjustmentsApi, config as configApi } from '../lib/api.js'
import type { AdjustmentRequestDto, AppliedAdjustmentDto } from '../lib/api.js'
import { getTokenPayload } from '../lib/auth.js'
import { useRealtime } from '../lib/realtime.js'
import { useI18n } from '../lib/i18n.js'
import {
  Badge,
  Button,
  Card,
  Empty,
  Modal,
  Spinner,
  Textarea,
  toast,
} from '../components/ui/index.js'
import { cn } from '../lib/utils.js'
import { intlLocaleFor } from '@self-restaurant/shared'

function ruleLabel(
  t: (k: string) => string,
  money: (cents: number | null | undefined) => string,
  type: string | null,
  value: number | null,
): string {
  if (!type) return t('admin.adjust.rule_none')
  if (type === 'none') return t('admin.adjust.type_none')
  if (type === 'discount_percent') return `-${value ?? 0}%`
  if (type === 'discount_amount') return `-${money(value)}`
  return `= ${money(value)}`
}

const STATUS_COLOR = {
  pending: 'yellow',
  approved: 'green',
  rejected: 'red',
  cancelled: 'gray',
} as const

function when(sec: number | null, lang: string): string {
  return sec ? new Date(sec * 1000).toLocaleString(lang) : '—'
}

export function AdjustmentApprovals() {
  const { t, lang } = useI18n()
  // #428: moeda do país do tenant e locale de formatação (língua + país)
  const { data: cfg } = useQuery({
    queryKey: ['config'],
    queryFn: () => configApi.get(),
    staleTime: 10 * 60_000,
  })
  const money = (cents: number | null | undefined) =>
    cents === null || cents === undefined
      ? '—'
      : new Intl.NumberFormat(intlLocaleFor(lang, cfg?.country_code), {
          style: 'currency',
          currency: cfg?.currency ?? 'BRL',
          minimumFractionDigits: 2,
        }).format(cents / 100)
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'pending' | 'audit'>('pending')
  const [rejecting, setRejecting] = useState<AdjustmentRequestDto | null>(null)
  const [note, setNote] = useState('')

  const { data: pending, isLoading } = useQuery({
    queryKey: ['adjustment-requests', 'pending'],
    queryFn: () => adjustmentsApi.inbox('pending'),
    refetchInterval: 30_000,
  })
  const { data: audit, isLoading: auditLoading } = useQuery({
    queryKey: ['adjustment-requests', 'audit'],
    queryFn: adjustmentsApi.audit,
    enabled: tab === 'audit',
  })

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['adjustment-requests'] })
  }, [queryClient])

  const onEvent = useCallback(
    (event: { type: string; payload?: Record<string, unknown> }) => {
      if (event.type === 'adjustment.requested') {
        const req = event.payload?.request as { table_number?: number } | undefined
        toast.info(t('admin.adjust.new_request_toast', { table: String(req?.table_number ?? '?') }))
        refresh()
      } else if (event.type === 'adjustment.decided') {
        refresh()
      }
    },
    [refresh, t],
  )
  const role = getTokenPayload()?.role ?? 'manager'
  const wsStatus = useRealtime({ role, onEvent })

  const approve = useMutation({
    mutationFn: (id: string) => adjustmentsApi.approve(id),
    onSuccess: () => {
      toast.success(t('admin.adjust.approved_toast'))
      refresh()
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.common.error')),
  })
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => adjustmentsApi.reject(id, note),
    onSuccess: () => {
      toast.info(t('admin.adjust.rejected_toast'))
      setRejecting(null)
      setNote('')
      refresh()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.common.error')),
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.adjust.page_title')}</h1>
          <p className="text-sm text-gray-500">
            {t('admin.adjust.page_hint')} ·{' '}
            <span
              data-testid="adjust-realtime"
              data-status={wsStatus}
              className={wsStatus === 'open' ? 'text-green-600' : 'text-amber-600'}
            >
              {wsStatus === 'open' ? t('admin.waiter.rt_on') : t('admin.waiter.rt_off')}
            </span>
          </p>
        </div>
        <div className="flex gap-1">
          {(['pending', 'audit'] as const).map((k) => (
            <button
              key={k}
              data-testid={`adjust-tab-${k}`}
              onClick={() => setTab(k)}
              className={cn(
                'px-3 py-2 rounded-lg border text-xs font-bold',
                tab === k
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-200 bg-white text-gray-600',
              )}
            >
              {k === 'pending'
                ? `${t('admin.adjust.tab_pending')} (${pending?.length ?? 0})`
                : t('admin.adjust.tab_audit')}
            </button>
          ))}
        </div>
      </div>

      {tab === 'pending' ? (
        isLoading ? (
          <Spinner className="w-6 h-6 text-indigo-500" />
        ) : (pending ?? []).length === 0 ? (
          <Empty title={t('admin.adjust.empty')} description={t('admin.adjust.empty_hint')} />
        ) : (
          <div className="space-y-3">
            {(pending ?? []).map((r) => (
              <Card key={r.id}>
                <div
                  className="flex flex-wrap items-start justify-between gap-3"
                  data-testid={`adjust-req-${r.id}`}
                >
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">
                      {t('admin.orders.table')} {r.table_label ?? r.table_number ?? '?'} ·{' '}
                      {r.target === 'order'
                        ? t('admin.adjust.target_order')
                        : t('admin.adjust.target_item')}
                    </p>
                    <p className="text-sm text-gray-700">
                      {ruleLabel(t, money, r.current_type, r.current_value)} →{' '}
                      <b>{ruleLabel(t, money, r.type, r.value)}</b>
                      {r.target === 'item' && (
                        <span className="text-gray-500">
                          {' '}
                          ({money(r.current_unit_price_cents)} →{' '}
                          {money(r.proposed_unit_price_cents)})
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('admin.adjust.requested_by', { name: r.requested_by_name ?? '—' })} ·{' '}
                      {when(r.requested_at, lang)}
                    </p>
                    {r.reason && <p className="text-xs text-gray-700">“{r.reason}”</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      data-testid={`adjust-approve-${r.id}`}
                      disabled={approve.isPending}
                      onClick={() => approve.mutate(r.id)}
                    >
                      {t('admin.adjust.approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid={`adjust-reject-${r.id}`}
                      onClick={() => setRejecting(r)}
                    >
                      {t('admin.adjust.reject')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : auditLoading ? (
        <Spinner className="w-6 h-6 text-indigo-500" />
      ) : (
        <AuditView
          applied={audit?.applied ?? []}
          requests={audit?.requests ?? []}
          lang={lang}
          money={money}
        />
      )}

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={t('admin.adjust.reject_title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              data-testid="adjust-reject-confirm"
              disabled={!note.trim() || reject.isPending}
              onClick={() => rejecting && reject.mutate({ id: rejecting.id, note: note.trim() })}
            >
              {t('admin.adjust.reject')}
            </Button>
          </>
        }
      >
        <Textarea
          label={t('admin.adjust.reject_reason')}
          data-testid="adjust-reject-note"
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Modal>
    </div>
  )
}

function AuditView({
  applied,
  requests,
  lang,
  money,
}: {
  applied: AppliedAdjustmentDto[]
  requests: AdjustmentRequestDto[]
  lang: string
  /** #428: moeda do país do tenant e locale de formatação */
  money: (cents: number | null | undefined) => string
}) {
  const { t } = useI18n()
  const closedWithoutApply = requests.filter((r) => r.status !== 'approved')
  return (
    <div className="space-y-6" data-testid="adjust-audit">
      <Card title={t('admin.adjust.audit_applied')}>
        {applied.length === 0 ? (
          <p className="text-sm text-gray-500">{t('admin.common.empty')}</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 px-2">{t('admin.adjust.col_when')}</th>
                <th className="py-2 px-2">{t('admin.adjust.col_change')}</th>
                <th className="py-2 px-2">{t('admin.adjust.col_total')}</th>
                <th className="py-2 px-2">{t('admin.adjust.col_requested')}</th>
                <th className="py-2 px-2">{t('admin.adjust.col_decided')}</th>
                <th className="py-2 px-2">{t('admin.adjust.col_reason')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {applied.map((a) => (
                <tr key={a.id} data-testid={`audit-applied-${a.id}`}>
                  <td className="py-2 px-2">{when(a.created_at, lang)}</td>
                  <td className="py-2 px-2">
                    {a.target === 'order'
                      ? t('admin.adjust.target_order')
                      : t('admin.adjust.target_item')}
                    : {ruleLabel(t, money, a.previous_type, a.previous_value)} →{' '}
                    {ruleLabel(t, money, a.new_type, a.new_value)}
                    {a.target === 'item' &&
                      ` (${money(a.previous_unit_price_cents)} → ${money(a.new_unit_price_cents)})`}
                  </td>
                  <td className="py-2 px-2">
                    {money(a.order_total_before_cents)} → {money(a.order_total_after_cents)}
                  </td>
                  <td className="py-2 px-2">
                    {a.request
                      ? `${a.request.requested_by_name ?? '—'} · ${when(a.request.requested_at, lang)}`
                      : '—'}
                  </td>
                  <td className="py-2 px-2">
                    {a.request?.auto_approved && (
                      <span
                        className="mr-1 rounded bg-emerald-100 px-1 text-emerald-800"
                        data-testid="audit-auto-approved"
                      >
                        {t('admin.adjust.auto_approved_badge')}
                      </span>
                    )}
                    {a.actor_name ?? '—'} (
                    {t(
                      a.actor_role === 'manager'
                        ? 'admin.users.role_manager'
                        : a.actor_role === 'waiter'
                          ? 'admin.users.role_waiter'
                          : 'admin.users.role_owner',
                    )}
                    )
                  </td>
                  <td className="py-2 px-2">{a.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card title={t('admin.adjust.audit_requests')}>
        {closedWithoutApply.length === 0 ? (
          <p className="text-sm text-gray-500">{t('admin.common.empty')}</p>
        ) : (
          <ul className="divide-y divide-gray-50 text-xs">
            {closedWithoutApply.map((r) => (
              <li
                key={r.id}
                className="py-2 flex flex-wrap gap-2 items-center"
                data-testid={`audit-req-${r.id}`}
              >
                <Badge color={STATUS_COLOR[r.status]}>{t(`admin.adjust.status_${r.status}`)}</Badge>
                <span>
                  {t('admin.orders.table')} {r.table_label ?? r.table_number ?? '?'} ·{' '}
                  {ruleLabel(t, money, r.type, r.value)}
                </span>
                <span className="text-gray-500">
                  {t('admin.adjust.requested_by', { name: r.requested_by_name ?? '—' })} ·{' '}
                  {when(r.requested_at, lang)}
                </span>
                {r.decided_at && (
                  <span className="text-gray-500">
                    {t('admin.adjust.decided_by', { name: r.decided_by_name ?? '—' })} ·{' '}
                    {when(r.decided_at, lang)}
                  </span>
                )}
                {r.reason && <span>“{r.reason}”</span>}
                {r.decision_note && <span className="text-red-700">— {r.decision_note}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
